import { Component, Injector, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Company, User } from '../../../auth/models';
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { CurrencyCollection, FuelPreferenceCollection, UnitsCollection } from "../../../common/models";
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import { Contract, ListContracts, Round, Tender } from '../../models';
import { ROUND_STATUSES } from "../../models/round";
import { TenderBidsService, TenderContractsService } from '../../services';
import { ContractFormModalComponent } from "../create-tender-contracts";

@Component({
    selector: 'jft-tender-contracts-awaiting-bids',
    templateUrl: './tender-contracts-awaiting-bids.component.html',
    styleUrls: ['./tender-contracts-awaiting-bids.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderContractsAwaitingBidsComponent implements OnInit, OnDestroy {
    @ViewChild('cancelLocationModal') cancelLocationModal: TemplateRef<any>;

    public loading: boolean = false;
    public tender: Tender;
    public contracts: ListContracts[] = [];
    public roundStatuses = ROUND_STATUSES;
    public contractToCancel: any;
    public cancellingLocation: boolean = false;

    public orderField: string;
    public order: string;

    private termSubscription: Subscription;
    private updateContractsSubscription: Subscription;

    // Collections for edit modal
    public currenciesCollection: CurrencyCollection;
    public unitsCollection: UnitsCollection;
    public fuelPreferenceCollection: FuelPreferenceCollection;
    public fullContract: Contract;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private contractsService: TenderBidsService,
        private contractService: TenderContractsService,
        private ngbModal: NgbModal,
        private injector: Injector,
        private loadingIndicator: LoadingIndicatorService,
        public company: Company,
        public user: User,
    ) {
    }

    public ngOnInit() {
        this.dataInit();
        this.orderInit();
        this.searchInit()
    }

    private dataInit() {
        let data = this.route.snapshot.data,
            parentData = this.route.parent.parent.snapshot.data;

        // Don't load contracts from resolver - let parent component's loadLocationTags() trigger it
        // This ensures saved tag filters are loaded before fetching data
        this.tender = parentData['tender'];

        // Subscribe to updates from parent component's filter changes
        this.updateContractsSubscription = this.contractsService.updateContractsObservable.subscribe(item => {
            // Only update contracts if it's not a stats_updated event
            if (!item || (typeof item === 'object' && (item as any).type !== 'stats_updated')) {
                this.refreshContractsWithFilters();
            }
        });
    }

    private refreshContractsWithFilters() {
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetchAwaitingBids(this.tender.id).subscribe((result: any) => {
            // Handle both paginated and non-paginated responses
            if (Array.isArray(result)) {
                this.contracts = result;
            } else {
                this.contracts = result.contracts;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (error) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(error, this.tender.name);
        });
    }

    private searchInit() {
        this.termSubscription = this.contractsService.termObservable
            .pipe(debounceTime(500))
            .pipe(distinctUntilChanged())
            .subscribe((term: string) => {
                this.loading = true;
                this.loadingIndicator.show();

                this.contractsService.setFilter(term);
                this.contractsService.fetchAwaitingBids(this.tender.id).subscribe((result: any) => {
                    // Handle both paginated and non-paginated responses
                    if (Array.isArray(result)) {
                        this.contracts = result;
                    } else {
                        this.contracts = result.contracts;
                    }
                    this.loading = false;
                    this.loadingIndicator.hide();
                }, (error) => {
                    this.loading = false;
                    this.loadingIndicator.hide();
                    this.toast.error(error, this.tender.name);
                });
            });
    }

    private orderInit() {
        let order = this.contractsService.getOrder();
        if (order) {
            this.order = order.startsWith('-') ? 'DESC' : 'ASC';
            this.orderField = order.startsWith('-') ? order.slice(1) : order;
        }
    }

    public sort($event: Event, field: string) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.orderField == field) {
            this.order = this.order == 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.order = 'ASC';
        }
        this.orderField = field;

        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.setOrder(this.orderField, this.order).fetchAwaitingBids(this.tender.id).subscribe((result: any) => {
            // Handle both paginated and non-paginated responses
            if (Array.isArray(result)) {
                this.contracts = result;
            } else {
                this.contracts = result.contracts;
            }
            this.loading = false;
            this.loadingIndicator.hide();
        }, (e) => {
            this.loading = false;
            this.loadingIndicator.hide();
            this.toast.error(e, this.tender.name);
        });
    }

    public onRoundExtend(round: Round) {
        this.updateContracts();
    }

    public updateContracts() {
        this.loading = true;
        this.loadingIndicator.show();

        this.contractsService.fetch(this.tender.id, 'awaiting-bids').subscribe(
            (data: ListContracts[]) => {
                this.contracts = data;
                this.toast.success(`Round has been extended`);
                this.loading = false;
                this.loadingIndicator.hide();
            }, (e) => {
                this.loading = false;
                this.loadingIndicator.hide();
                this.toast.error(e, this.tender.name);
            }
        );
    }

    public ngOnDestroy(): void {
        this.termSubscription.unsubscribe();
        this.updateContractsSubscription.unsubscribe();
    }

    public isDateExpired(date) {
        return DateTimeHelper.isDateExpired(date)
    }

    public editLocation(contract: any) {
        this.contractService.fetchItem(this.tender.id, contract.id).subscribe((contract: Contract) => {
            this.fullContract = contract;
            this._editLocation(contract);
        });
    }

    public _editLocation(contract: any) {
        const dialogRef = this.ngbModal.open(ContractFormModalComponent, {
            injector: Injector.create([
                {provide: Contract, useValue: contract},
                {provide: Tender, useValue: this.tender},
                {provide: CurrencyCollection, useValue: this.currenciesCollection},
                {provide: UnitsCollection, useValue: this.unitsCollection},
                {provide: FuelPreferenceCollection, useValue: this.fuelPreferenceCollection}
            ], this.injector),
            size: 'lg',
            backdrop: 'static'
        })

        dialogRef.componentInstance.disableLocation = true;
        dialogRef.result.then((contract: Contract) => {
            this.updateContractsAfterEdit();
        }, (error) => {

        });
    }

    public updateContractsAfterEdit() {
        this.contractsService.fetch(this.tender.id, 'awaiting-bids').subscribe(
            (data: ListContracts[]) => {
                this.contracts = data;
                this.toast.success(`Location updated successfully`);
            }, (e) => {this.toast.error(e, this.tender.name)}
        );
    }

    public showCancelConfirmation(contract: any) {
        this.contractToCancel = contract;
        this.ngbModal.open(this.cancelLocationModal);
    }

    public confirmCancelContract() {
        if (this.contractToCancel) {
            // Check if location is awarded - shouldn't happen due to disabled button, but safety check
            if (this.contractToCancel.currentRound?.status === this.roundStatuses.AWARDED) {
                this.toast.error('Cannot cancel awarded location. Please withdraw the award first.');
                return;
            }

            this.cancellingLocation = true;
            this.contractService.cancel(this.tender, this.contractToCancel).subscribe((contract) => {
                this.cancellingLocation = false;
                this.updateContractsAfterEdit();
                this.ngbModal.dismissAll();
                this.contractToCancel = null;
                this.toast.success('Location has been cancelled');
            }, (e) => {
                this.cancellingLocation = false;
                this.toast.error(e);
                this.ngbModal.dismissAll();
                this.contractToCancel = null;
            });
        }
    }
}
